import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Box, Card, CardHeader, Container, IconButton, } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Add, Check, Close, Edit } from '@mui/icons-material';
import { VesselModel } from 'models/VesselModel';
import useVesselModals from 'modules/vessels/hooks/useVesselModals';
import auth from 'recoil/atoms/auth';
import vesselList from 'recoil/atoms/vesselList';


const VesselsInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const vessels = useRecoilValue(vesselList);
    const { openAddVesselModal, openEditVesselModal } = useVesselModals();

    const addButton = my.permissions.has('vessels create') ? (
        <IconButton
            aria-label="add vessel"
            onClick={openAddVesselModal}
        >
            <Add/>
        </IconButton>
    ): null;

    const editButton = (vessel: VesselModel) => (
        <GridActionsCellItem
            icon={<Edit />}
            label={t('edit')}
            onClick={() => openEditVesselModal(vessel)}
        />
    );

    const columns: GridColDef<VesselModel>[] = [
        {
            field: 'name',
            headerName: t('name'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'code',
            headerName: t('code'),
            flex: 1,
            minWidth: 50,
        },
        {
            field: 'capacity',
            headerName: t('capacity'),
            type: 'number',
            flex: 1,
            minWidth: 70,
        },
        {
            field: 'phone',
            headerName: t('phone'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'email',
            headerName: t('email'),
            flex: 1,
            minWidth: 160,
        },
        {
            field: 'captainName',
            headerName: t('captain_name'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'captainSignature',
            headerName: t('signature'),
            type: 'boolean',
            flex: 1,
            renderCell: params => {
                return params.value ? (
                    <Check sx={{color: 'success.light'}}/>
                ) : (
                    <Close sx={{color: 'error.light'}}/>
                );
            },
        },
        {
            field: 'captainStamp',
            headerName: t('stamp'),
            type: 'boolean',
            flex: 1,
            renderCell: params => {
                return params.value ? (
                    <Check sx={{color: 'success.light'}}/>
                ) : (
                    <Close sx={{color: 'error.light'}}/>
                );
            },
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: params => {
                const act = [];
                if (my.permissions.has('vessels update')) act.push(editButton(params.row));
                return act;
            },
        },
    ];

    return (
        <Container maxWidth="lg" sx={{mt: 2}}>
            <Card>
                <CardHeader
                    title={t('vessels')}
                    action={addButton}
                />
                <Box sx={{height: 'calc(100vh - 148px)', padding: '0 1rem'}}>
                    <DataGrid
                        rows={vessels}
                        columns={columns}
                    />
                </Box>
            </Card>
        </Container>
    );
};

const Vessels = () => {
    return (
        <Suspense fallback={null}>
            <VesselsInner />
        </Suspense>
    )
}

export default Vessels;
